// =============================================================================
// Neural Grid — Conway's Game of Life on CSS Grid
// =============================================================================
// A subtle Game of Life animation using DOM elements on a CSS-defined grid.
// Each life cell = 2x2 grid cells. Grid styling is in _grid.scss.

const GRID_COLUMNS = 80; // Must match CSS --grid-columns
const LIFE_CELL_SIZE = 2; // Each life cell spans 2x2 grid cells (48 life columns)
const LIFE_ROWS_MAX = 150; // Fixed max rows — CSS handles actual grid extension
const GENERATION_INTERVAL = 2000; // ms between generations

// Random spawning keeps the animation going but deviates from pure Conway rules
const ENABLE_RANDOM_SPAWNING = true; // Set to true for infinite animation
const SPAWN_PROBABILITY = 0.04; // Random spawn chance near clusters (only used if enabled)

interface Cell {
    alive: boolean;
    element: HTMLDivElement;
}

class NeuralGrid {
    private container: HTMLElement | null;
    private cells: Cell[][] = [];
    private lifeCols: number = 0;
    private lifeRows: number = 0;
    private lastGeneration: number = 0;
    private animationId: number = 0;

    constructor() {
        this.container = document.getElementById("life-grid");

        if (!this.container) {
            console.warn("NeuralGrid: #life-grid container not found");
            return;
        }

        this.calculateDimensions();
        this.createCells();
        this.seedRandom();
        this.animate(0);
    }

    private calculateDimensions(): void {
        this.lifeCols = Math.floor(GRID_COLUMNS / LIFE_CELL_SIZE);
        this.lifeRows = LIFE_ROWS_MAX; // Fixed max — CSS grid-auto-rows handles layout
    }

    private createCells(): void {
        if (!this.container) return;

        this.container.innerHTML = "";
        this.cells = [];

        for (let y = 0; y < this.lifeRows; y++) {
            this.cells[y] = [];
            for (let x = 0; x < this.lifeCols; x++) {
                const element = document.createElement("div");
                element.className = "life-cell";

                // Position on CSS grid: each life cell spans 2x2 grid cells
                const gridCol = x * LIFE_CELL_SIZE + 1;
                const gridRow = y * LIFE_CELL_SIZE + 1;
                element.style.gridColumn = `${gridCol} / span ${LIFE_CELL_SIZE}`;
                element.style.gridRow = `${gridRow} / span ${LIFE_CELL_SIZE}`;

                this.container.appendChild(element);

                this.cells[y][x] = {
                    alive: false,
                    element,
                };
            }
        }
    }

    // ==========================================================================
    // Seeding — Initial random cell population
    // ==========================================================================

    private seedRandom(): void {
        const clusterCount = Math.floor((this.lifeCols * this.lifeRows) / 800);

        for (let i = 0; i < clusterCount; i++) {
            const cx = Math.floor(Math.random() * this.lifeCols);
            const cy = Math.floor(Math.random() * this.lifeRows);
            const radius = Math.floor(Math.random() * 3) + 2;

            for (let dy = -radius; dy <= radius; dy++) {
                for (let dx = -radius; dx <= radius; dx++) {
                    const x = cx + dx;
                    const y = cy + dy;
                    if (
                        x >= 0 &&
                        x < this.lifeCols &&
                        y >= 0 &&
                        y < this.lifeRows
                    ) {
                        // Higher density within clusters (was 0.12)
                        if (Math.random() < 0.45) {
                            this.setCellState(x, y, true);
                        }
                    }
                }
            }
        }
    }

    // ==========================================================================
    // Cell State Management
    // ==========================================================================

    private setCellState(x: number, y: number, alive: boolean): void {
        const cell = this.cells[y]?.[x];
        if (!cell) return;

        cell.alive = alive;
        cell.element.classList.toggle("alive", alive);
    }

    // ==========================================================================
    // Conway's Game of Life Algorithm
    // ==========================================================================

    private countNeighbors(x: number, y: number): number {
        let count = 0;
        for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
                if (dx === 0 && dy === 0) continue;
                // Toroidal wrapping (edges connect)
                const nx = (x + dx + this.lifeCols) % this.lifeCols;
                const ny = (y + dy + this.lifeRows) % this.lifeRows;
                if (this.cells[ny]?.[nx]?.alive) {
                    count++;
                }
            }
        }
        return count;
    }

    private nextGeneration(): void {
        const newStates: boolean[][] = [];

        // Calculate next state for all cells
        for (let y = 0; y < this.lifeRows; y++) {
            newStates[y] = [];
            for (let x = 0; x < this.lifeCols; x++) {
                const cell = this.cells[y]?.[x];
                if (!cell) continue;

                const neighbors = this.countNeighbors(x, y);

                // Conway's Game of Life rules:
                // 1. Live cell with 2-3 neighbors survives
                // 2. Dead cell with exactly 3 neighbors becomes alive
                // 3. All other cells die or stay dead
                if (cell.alive) {
                    newStates[y][x] = neighbors === 2 || neighbors === 3;
                } else {
                    // Pure Conway: only birth with exactly 3 neighbors
                    // Random spawning near existing cells (1-2 neighbors) keeps clusters alive
                    const nearCluster = neighbors >= 1 && neighbors <= 2;
                    const randomSpawn =
                        ENABLE_RANDOM_SPAWNING &&
                        nearCluster &&
                        Math.random() < SPAWN_PROBABILITY;
                    newStates[y][x] = neighbors === 3 || randomSpawn;
                }
            }
        }

        // Apply new states
        for (let y = 0; y < this.lifeRows; y++) {
            for (let x = 0; x < this.lifeCols; x++) {
                const cell = this.cells[y]?.[x];
                if (!cell) continue;

                const newState = newStates[y][x];
                if (newState !== cell.alive) {
                    this.setCellState(x, y, newState);
                }
            }
        }
    }

    // ==========================================================================
    // Animation Loop
    // ==========================================================================

    private animate = (timestamp: number): void => {
        const deltaTime = timestamp - this.lastGeneration;

        if (deltaTime >= GENERATION_INTERVAL) {
            this.nextGeneration();
            this.lastGeneration = timestamp;
        }

        this.animationId = requestAnimationFrame(this.animate);
    };

    destroy(): void {
        cancelAnimationFrame(this.animationId);
        if (this.container) {
            this.container.innerHTML = "";
        }
    }
}

// Initialize when DOM is ready
let neuralGrid: NeuralGrid | null = null;

export function initNeuralGrid(): void {
    if (neuralGrid) return;

    // Respect reduced motion preference
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return;
    }

    neuralGrid = new NeuralGrid();
}

export function destroyNeuralGrid(): void {
    if (neuralGrid) {
        neuralGrid.destroy();
        neuralGrid = null;
    }
}
